/* eslint-disable */
import type { IPostCategory } from '@/interfaces/analytics'
import { DateRange, Value } from '@/shared/ui/date-picker/date-picker'

export const throttle = <T extends (...args: any[]) => void>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let lastFunc: NodeJS.Timeout | null = null
  let lastRan: number | null = null

  return function (this: any, ...args: Parameters<T>) {
    const context = this
    if (!lastRan) {
      func.apply(context, args)
      lastRan = performance.now()
    } else {
      if (lastFunc) {
        clearTimeout(lastFunc)
      }
      lastFunc = setTimeout(
        () => {
          if (performance.now() - lastRan! >= limit) {
            func.apply(context, args)
            lastRan = performance.now()
          }
        },
        limit - (performance.now() - lastRan)
      )
    }
  }
}

export function debounce<A = unknown, R = void>(fn: (args: A) => R, ms: number): [(args: A) => Promise<R>, () => void] {
  let timer: NodeJS.Timeout

  const debouncedFunc = (args: A): Promise<R> =>
    new Promise(resolve => {
      if (timer) {
        clearTimeout(timer)
      }

      timer = setTimeout(() => {
        resolve(fn(args))
      }, ms)
    })

  const teardown = () => clearTimeout(timer)

  return [debouncedFunc, teardown]
}

export const getCategoryId = (filter: IPostCategory | null) => {
  return filter ? (filter?.id as string | undefined) : undefined
}

export const addLeadingZero = (value: number | string, targetLength: number = 2): string => {
  return value.toString().padStart(targetLength, '0')
}

export const stringifyDateToISO = (date?: Date | null) => {
  if (!date) return ''

  return `${date.getFullYear()}-${addLeadingZero(
    date.getMonth() + 1
  )}-${addLeadingZero(date.getDate())}T${addLeadingZero(
    date.getHours()
  )}:${addLeadingZero(date.getMinutes())}:${addLeadingZero(date.getSeconds())}`
}

export const getDateRange = (range: Value) => {
  if (!Array.isArray(range)) return

  return range.map(date => {
    return stringifyDateToISO(date)
  }) as [string, string] | undefined
}

export const getDateRangeFormat = (range: Value) => {
  if (!Array.isArray(range)) return

  return `${range[0]?.toLocaleDateString() ?? ''} — ${range[1]?.toLocaleDateString() ?? ''}`
}

export const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min)
  max = Math.floor(max)
  if (min === max) return min
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export const getFileExtension = (fileName?: string) => {
  if (!fileName) return ''
  const extension = fileName.split('.').pop()
  return extension ? extension.toLowerCase() : ''
}

export function normalizeFilterParam(param?: string | number | string[] | number[]) {
  if (!param) return []
  return Array.isArray(param) ? param.filter(i => !!i) : [param]
}
